import React, { useRef, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import { svgToIcon } from '../utils/svg';
import { type Instance as TippyInstance } from 'tippy.js/index';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import ToggleToolButton from '../toolbars/ToggleToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import config from '../assets/icons/config.svg';
import background from '../assets/icons/styleShowBackground.svg';
import outline from '../assets/icons/styleShowOutline.svg';
import icons from '../assets/icons/wireframeIcon.svg';
import separators from '../assets/icons/styleShowSeparator.svg';
import browserBar from '../assets/icons/styleShowBrowserBar.svg';
import framed from '../assets/icons/styleShowFrame.svg';
import statusBar from '../assets/icons/styleShowBattery.svg';
import scrollbarOutline from '../assets/icons/styleScrollBarOutline.svg';
import paginationButtonIcons from '../assets/icons/styleShowPaginationButtonIcons.svg';
import paginationButtonLabel from '../assets/icons/styleShowPaginationButtonLabel.svg';
import paginationEllipses from '../assets/icons/styleShowPaginationEllipses.svg';
import progressStepText from '../assets/icons/styleShowProgressStepText.svg';
import progressStepNumbers from '../assets/icons/styleShowProgressStepNumbers.svg';

interface PopupProps {
  options: {
    [key: string]: boolean;
  }[];
  setOptions: React.Dispatch<
    React.SetStateAction<
      {
        option: any;
      }[]
    >
  >;
  selection: fabric.Object;
  tool: typeof ToolStyleFlags;
}

interface Flag {
  name: string;
  key: string;
  icon: any;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ selection, tool, setOptions }) => {
    const controller = useController(WhiteboardController);

    const toggleOption = option => {
      selection.set(option, !selection[option]);
      setOptions(prev => ({ ...prev, [option]: selection[option] }));
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        <ToolbarMenu columns={5}>
          {tool.flags.map(({ name, key, icon }) => {
            const active = selection[key];
            const enabled = selection.styleFlagEnabled(key);
            return (
              <ToggleToolButton
                key={key}
                name={name}
                icon={icon}
                active={active}
                enabled={enabled}
                onClick={() => toggleOption(key)}
              />
            );
          })}
        </ToolbarMenu>
      </div>
    );
  }
);

const StyleFlagButton: ToolButtonComponent<
  fabric.Object,
  typeof ToolStyleFlags
> = ApplicationView(({ selection, tool }) => {
  const tippyRef = useRef<TippyInstance>();

  const [options, setOptions] = useState(
    tool.flags.map(({ key }) => {
      return { [key]: selection[key] };
    })
  );

  return (
    <ToolButton
      popup={
        <Popup
          options={options}
          setOptions={setOptions}
          selection={selection}
          tool={tool}
        />
      }
      onClick={() =>
        tippyRef.current?.state.isShown && tippyRef.current?.hide()
      }
      tool={tool}
      tippyRef={tippyRef}
    >
      {svgToIcon(config)}
    </ToolButton>
  );
});

class ToolStyleFlags extends Tool {
  static toolName = 'Options';
  static flags: Flag[];

  static buttonComponent() {
    return StyleFlagButton;
  }
}

class ToolDevicePhoneTabletStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [
    { name: 'Framed', key: 'showFramed', icon: framed },
    { name: 'Status Bar', key: 'showStatusBar', icon: statusBar },
  ];
}

class ToolDeviceLaptopStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [
    { name: 'Browser bar', key: 'showBrowserBar', icon: browserBar },
  ];
}

class ToolMultiSelectStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [{ name: 'Icons', key: 'showIcons', icon: icons }];
}

class ToolNavMenuStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [
    { name: 'Icons', key: 'showIcons', icon: icons },
    { name: 'Separators', key: 'showSeparators', icon: separators },
  ];
}

class ToolPillStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [
    { name: 'Outline', key: 'showOutline', icon: outline },
    { name: 'Background', key: 'showBackground', icon: background },
  ];
}

class ToolScrollBarStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [
    { name: 'Outline', key: 'showOutline', icon: scrollbarOutline },
  ];
}

class ToolPaginationStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [
    { name: 'Button icon', key: 'showIcons', icon: paginationButtonIcons },
    { name: 'Button text', key: 'showButtonText', icon: paginationButtonLabel },
    { name: 'Ellipses', key: 'showEllipses', icon: paginationEllipses },
  ];
}

class ToolProgressStepperStyleFlags extends ToolStyleFlags {
  static flags: Flag[] = [
    { name: 'Icons', key: 'showIcons', icon: icons },
    { name: 'Text', key: 'showStepText', icon: progressStepText },
    { name: 'Numbers', key: 'showStepNumbers', icon: progressStepNumbers },
  ];
}

export {
  ToolDeviceLaptopStyleFlags,
  ToolDevicePhoneTabletStyleFlags,
  ToolMultiSelectStyleFlags,
  ToolNavMenuStyleFlags,
  ToolPillStyleFlags,
  ToolScrollBarStyleFlags,
  ToolPaginationStyleFlags,
  ToolProgressStepperStyleFlags,
};
